import React from 'react';
import BeaconProChart, {
  DefaultMenuButton
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { KeywordFilter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import {
  GaugeSummaryCard,
  GaugeSummaryCardSkeleton
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/GaugeSummaryCard';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { KeywordsPerformanceSummaryAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeywordsPerformanceSummaryAdapter';
import { useContentChartSubtitles } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useDynamicSubtitle';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';

const SUBHEADERS = {
  [KeywordFilter.Usage]: {
    default: 'AVG NUMBER OF KEYWORDS',
    product: 'NUMBER OF KEYWORDS'
  },
  [KeywordFilter.Productivity]: {
    default: 'AVG TRAFFIC GENERATING KEYWORDS',
    product: 'PRODUCTIVE KEYWORDS'
  },
  [KeywordFilter.Popularity]: {
    default: 'AVG SEARCH TRAFFIC VOLUME',
    product: 'SEARCH TRAFFIC VOLUME'
  }
};

interface KeywordsGaugeCardsProps {
  title: string;
  predefinedColor?: string;
  queryFn: () => Promise<any>;
  onClickValue: (filter: KeywordFilter) => void;
  selectedFilter: KeywordFilter;
  renderSubtitle: () => string | React.ReactNode;
}

export const KeywordsGaugeCards = (props: KeywordsGaugeCardsProps) => {
  const { title, predefinedColor, queryFn, onClickValue, selectedFilter, renderSubtitle } = props;
  const { generateKeywordPerformanceSubtitle, generateKeywordSubtitleForMetric } = useContentChartSubtitles();
  const metricFormatter = useMetricFormatter();
  const entityType = useMainEntityType();

  return (
    <BeaconProChart queryFn={queryFn} name={`keywords-gauge-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <BeaconProChart.Header menuButton={() => <DefaultMenuButton excludeActions={['exportCsv']} />}>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle render={renderSubtitle ?? (() => generateKeywordPerformanceSubtitle())} />

      <BeaconProChart.Visualization
        render={(adapter: KeywordsPerformanceSummaryAdapter, isLoading: boolean) => {
          if (isLoading) {
            return (
              <Flex marginTop="32px" gap="md">
                {[...Array(3)].map((_, index) => (
                  <GaugeSummaryCardSkeleton key={index} />
                ))}
              </Flex>
            );
          }

          const data = adapter.transform();
          return (
            <Flex marginTop="32px" gap="md">
              {data.map((item) => {
                return (
                  <GaugeSummaryCard
                    key={item.title}
                    title={item.title}
                    predefinedColor={predefinedColor}
                    subheader={SUBHEADERS[item.filterKey]?.[entityType] ?? SUBHEADERS[item.filterKey]?.default}
                    subtitle={generateKeywordSubtitleForMetric({
                      metric: item.filterKey,
                      formattedValue: metricFormatter(item.value, METRICTYPE.VOLUME, {
                        showFullValue: false,
                        decimalPlaces: 0
                      })
                    })}
                    percentage={item.percentage}
                    change={item.change}
                    value={item.value}
                    onClick={() => onClickValue(item.filterKey)}
                    isSelected={selectedFilter === item.filterKey}
                  />
                );
              })}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
